import React, { useRef, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import get from 'lodash.get';
import clsx from 'clsx';
import { Link } from 'acq-components/lib/base-components/Link/Link';
import {
  getPdpLinks,
  getCardWithPznData,
  getAddAnEmployeeDtmEvents,
  getAddAnEmployeeHoverText
} from 'page_modules/business-pdp/redux';
import { getIsSuppCardsModalOpen } from 'src/universal/redux/page';
import { ScrollActivator } from 'acq-components/lib/utils/ScrollActivator/ScrollActivator';
import {
  getIsUserCard,
  isGCSMember,
  isLegacyGcsCardMember
} from 'src/universal/redux/cardMemberInfo';
import { hasPznDataArrived } from 'src/universal/redux/pznData';
import { createDtmAndTrackActionHandler } from 'src/universal/utils/analyticsTracking';
import TcLinks from 'page_modules/business-pdp/components/TcLinks';
import styles from './__styles__/stickyApplyBar.scss?modules';
import tcLinksStyles from './__styles__/tcLinks.scss?modules';
import { Size, Icon } from '@americanexpress/aqx-components';
import { suppModalState } from 'page_modules/business-pdp/utils/setModalStateAction';
import tooltipStyles from 'src/universal/components/SuppModal/components/styles/toolTipWrapper.scss?modules';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import { getUpgradeCtaProps } from 'src/universal/utils/orchestraUtils';
import { getWinWidth } from 'core_modules/bootstrap/browsers/browser-detection';
import ApplyAndUpgradeCtas from 'src/universal/components/ApplyOrUpgradeCta';
import { needFullPageHero } from 'page_modules/business-pdp/components/Hero/redux';

const applyBar = React.memo(function ApplyBar({
  dispatch,
  isCardMemberCard,
  cardData,
  cardLinks,
  suppModalOpen,
  isGCSMember,
  isGCSLegacy,
  addAnEmployeeScrollDtm,
  addEmpHoverText,
  pznArrived
}) {
  const { needUpgradeCta } = getUpgradeCtaProps(
    cardLinks,
    cardData.pznOffer,
    isGCSMember || isGCSLegacy
  );
  const tncProps = {
    styles: tcLinksStyles
  };
  if (needUpgradeCta) {
    tncProps.customKeys = ['offerTermsUpgrade', 'ratesFeesUpgrade'];
  }

  const initialHideVal = getWinWidth() >= 1024 ? false : true;
  const needHidingOnLoad = useRef(initialHideVal);
  const scrollHandlerTriggered = useRef(false);
  const applyBarWrap = useRef(null);
  const scrollHandler = () => {
    scrollHandlerTriggered.current = true;
    if (needHidingOnLoad.current === true) {
      return true;
    }
    const targetsToAvoid = [
      document.querySelector('div[class*="purify_RIBApply__ctaHolder--'),
      document.querySelector('[data-qe-id=CrossSellBanner'),
      document.querySelector('[data-qe-id=crossSellCardTiles]'),
      document.querySelector(
        '.sc_mo_call-to-actions-with-links_verticalCta > .sc_mo_call-to-actions-with-links_itemContainer:first-of-type'
      ),
      document.querySelector('#pdpHeroOfferBoxOuter button.sc_at_button_btn'),
      document.querySelector(
        '[id*="purify_offerBox__pdpHeroOfferBox--"] button.sc_at_button_btn'
      ),
      document.querySelector(
        '._sideBySide_1iia3_10 > ._itemContainerWithEvenWidth_1iia3_7'
      ),
      document.querySelector('div[class*="purify_g134Hero"] .sc_at_button_btn'),
      document.querySelector('._sideBySide_1iia3_10 > ._itemContainer_1iia3_4'),
      document.querySelector(
        'div[class*="purify_hero__container--"] div[class*="purify_hero__contentContainer--"] ._btn_1j0tc_16'
      ),
      document.querySelector('div[class*="purify_index__ctas--"]'),
      document.querySelector('span[class*="_verticalCta_"]'),
      document.querySelector('#pdp-hero-apply-now'),
      document.querySelector(
        'div[class*="purify_index__dynAppBannerOuter--"] button[class*="purify_index__ctaSecondary--"]'
      )
    ];

    return targetsToAvoid.some((targetToAvoid, i) => {
      if (
        targetToAvoid &&
        targetToAvoid.getBoundingClientRect &&
        applyBarWrap.current
      ) {
        const applyBarWrapRect = applyBarWrap.current.getBoundingClientRect();
        const targetRect = targetToAvoid.getBoundingClientRect();

        const subNavHeight = 60;
        const targetTopAbovePageViewportBottom =
          targetRect.top < window.innerHeight - applyBarWrapRect.height;
        const targetTopBelowSubNav =
          targetRect.top + targetRect.height > subNavHeight;
        const targetBottomAbovePageViewportBottom =
          targetRect.bottom + applyBarWrapRect.height > targetRect.height;

        switch (i) {
          case 0:
          case 1:
          case 3:
          case 4:
          case 5:
          case 6:
          case 8:
          case 10:
          case 11:
          case 12:
          case 13:
          case 9: {
            if (targetTopAbovePageViewportBottom && targetTopBelowSubNav) {
              return true;
            }
            break;
          }
          case 2: {
            if (
              targetTopAbovePageViewportBottom &&
              targetTopBelowSubNav &&
              targetBottomAbovePageViewportBottom
            ) {
              return true;
            }
            break;
          }
        }
      }
      return false;
    });
  };

  const addEmpLink = cardLinks.addAnEmployee || {};

  const toggleModal = () => {
    dispatch(suppModalState(!suppModalOpen, 'bottom'));
  };

  useLayoutEffect(
    () => {
      needHidingOnLoad.current = false;
      document.documentElement.scrollTop = document.documentElement.scrollTop + 1;
    },
    [pznArrived]
  );

  return (
    <ScrollActivator onScroll={scrollHandler}>
      {shouldHide => {
        const shouldHideVal = !scrollHandlerTriggered.current
          ? needHidingOnLoad.current
          : shouldHide;
        const wrapperClass = clsx(styles.stickyApplyBar, {
          [styles.shouldHide]: shouldHideVal
        });
        return pznArrived ? (
          <div className={wrapperClass} ref={applyBarWrap}>
            <div
              className={clsx(styles.buttonWrap, {
                [styles.hasUpgradeCta]: needUpgradeCta
              })}
            >
              <ApplyAndUpgradeCtas
                shortName={cardData?.shortName}
                allowUpgradeCta={true}
              />
            </div>
            {isCardMemberCard && (
              <div className={styles.addEmpRow}>
                <LazyScrollMetricTracker scrollDtm={addAnEmployeeScrollDtm.applyBar}>
                  <Link
                    href={addEmpLink.url}
                    className={styles.addEmpCard}
                    title={addEmpHoverText}
                    onClick={() => {
                      createDtmAndTrackActionHandler({
                        dtmObj: addEmpLink.dtm,
                        actionTracking: addEmpLink.actionTracking,
                        appendToActionAttr: '_ApplyBar'
                      })();
                    }}
                  >
                    {addEmpLink.text}
                  </Link>
                </LazyScrollMetricTracker>
                {isGCSMember && (
                  <span
                    className={clsx(styles.addEmployeeInfo, tooltipStyles.pdpIcon)}
                    onClick={toggleModal}
                  >
                    <Icon
                      directory="dls"
                      imageName="dls-icon-info-tooltip"
                      $size={Size.XS}
                    />
                  </span>
                )}
              </div>
            )}
            <TcLinks {...tncProps} />
          </div>
        ) : null;
      }}
    </ScrollActivator>
  );
});

const mapStateToProps = (state, ownProps) => {
  const urlEep = get(ownProps, 'params.eep') || '';
  const currentCard = getCardWithPznData(state);
  const { pmcCode } = currentCard;
  return {
    cardLinks: getPdpLinks(urlEep)(state),
    cardData: currentCard,
    isCardMemberCard: !!getIsUserCard(pmcCode)(state),
    suppModalOpen: getIsSuppCardsModalOpen(state),
    addAnEmployeeScrollDtm: getAddAnEmployeeDtmEvents(state),
    addEmpHoverText: getAddAnEmployeeHoverText(state),
    isGCSMember: isGCSMember(state),
    isGCSLegacy: isLegacyGcsCardMember(state),
    pznArrived: hasPznDataArrived(state),
    hasFullPageHero: needFullPageHero(state)
  };
};

export const StickyApplyBar = withRouter(connect(mapStateToProps)(applyBar));
